<template>
  <div>
  <v-dialog v-model="opened" width="720" persistent>
    <v-card class="delivery-confirmation-modal">
      <div class="delivery-confirmation-head">
        <v-avatar size="36" color="#333">
          <v-icon color="#f2f2f2">mdi-truck-delivery-outline</v-icon>
        </v-avatar>
        <div>
          <strong>Confirmar nota de entrega</strong>
          <small>Informe os responsáveis antes de emitir a nota.</small>
        </div>
        <v-spacer />
        <v-btn icon @click="close">
          <v-icon color="grey">mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="pt-4">
        <v-form ref="payer_form">
          <div class="expande-horizontal wrap">
            <v-flex xs12 md4 class="pr-2">
              <v-text-field
                ref="cepInput"
                v-model="address.zip_code"
                dense
                filled
                label="CEP"
                placeholder="########"
                :color="$theme.primary"
                :rules="[v => !!v || 'Preencha este campo']"
                v-mask="['########']"
                @input="searchCep"
              />
            </v-flex>
            <v-flex xs12 md6 class="pr-2">
              <v-text-field
                v-model="address.street"
                dense
                filled
                label="Endereço"
                :color="$theme.primary"
                :rules="[v => !!v || 'Preencha este campo']"
              />
            </v-flex>
            <v-flex xs12 md2>
              <v-text-field
                ref="addressNumber"
                v-model="address.number"
                dense
                filled
                label="Nº"
                :color="$theme.primary"
                :rules="[v => !!v || 'Preencha este campo']"
              />
            </v-flex>
          </div>

          <div class="expande-horizontal wrap">
            <v-flex xs12 md4 class="pr-2">
              <v-text-field
                v-model="address.city"
                dense
                filled
                label="Cidade"
                placeholder="ex: Macapá"
                :color="$theme.primary"
              />
            </v-flex>
            <v-flex xs12 md4 class="pr-2">
              <v-text-field
                v-model="address.state"
                dense
                filled
                label="Estado"
                placeholder="ex: Amapá"
                :color="$theme.primary"
              />
            </v-flex>
            <v-flex xs12 md4>
              <v-text-field
                v-model="address.district"
                dense
                filled
                label="Bairro"
                placeholder="ex: Zerão"
                :color="$theme.primary"
              />
            </v-flex>
          </div>

          <v-autocomplete
            v-model="responsibleIds"
            :items="get_funcionarios.docs"
            item-text="nome"
            item-value="_id"
            label="Responsáveis pela entrega"
            placeholder="Selecione um ou mais usuários"
            filled
            dense
            multiple
            chips
            deletable-chips
            :color="$theme.primary"
            :rules="[v => (v && v.length > 0) || 'Selecione ao menos um responsável']"
          />

          <div class="expande-horizontal wrap">
            <v-flex xs12 md4 class="pr-2">
              <v-menu
                ref="menu"
                v-model="menu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="visitDate"
                    label="Data da entrega"
                    filled
                    dense
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    :color="$theme.primary"
                    :rules="[v => !!v || 'Preencha este campo']"
                  />
                </template>
                <v-date-picker
                  v-model="dateUnformatted"
                  no-title
                  :color="$theme.primary"
                  @input="formatDate"
                />
              </v-menu>
            </v-flex>
            <v-flex xs12 md3 class="pr-2">
              <v-text-field
                v-model="visitHour"
                filled
                dense
                label="Hora"
                v-mask="['##:##']"
                :color="$theme.primary"
                :rules="[
                  v => !!v || 'Preencha este campo',
                  v => (v && v.length === 5) || 'Preencha este campo'
                ]"
              />
            </v-flex>
            <v-flex xs12 md5>
              <v-autocomplete
                v-model="vehicle"
                :items="vehicleItems"
                item-text="label"
                item-value="_id"
                label="Veículo"
                placeholder="Opcional"
                filled
                clearable
                dense
                :color="$theme.primary"
              />
            </v-flex>
          </div>

          <v-textarea
            v-model="description"
            dense
            filled
            rows="2"
            label="Observação da entrega"
            :color="$theme.primary"
          />
        </v-form>
      </v-card-text>

      <v-card-actions class="px-4 pb-4">
        <v-spacer />
        <v-btn text @click="close">Cancelar</v-btn>
        <v-btn
          depressed
          :loading="saving"
          :color="$theme.primary"
          class="delivery-primary-button"
          @click="saveAddress"
        >
          Confirmar e emitir nota
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="existingNoteDialog" width="560" persistent>
    <v-card class="delivery-confirmation-modal delivery-choice-modal">
      <div class="delivery-confirmation-head">
        <v-avatar class="delivery-choice-head-icon" size="38">
          <v-icon size="21">mdi-file-document-alert-outline</v-icon>
        </v-avatar>
        <div>
          <strong>Nota de entrega existente</strong>
          <small>A nota {{ activeDeliveryNoteNumber }} já foi emitida.</small>
        </div>
      </div>
      <v-card-text class="delivery-choice-content">
        <button
          class="delivery-choice-option"
          :disabled="saving"
          @click="startNewDeliveryNote"
        >
          <span class="delivery-choice-icon new-note">
            <v-icon size="23">mdi-file-plus-outline</v-icon>
          </span>
          <span class="delivery-choice-copy">
            <strong>Nota Nova</strong>
            <small>Cancela a nota atual, cancela o agendamento vinculado e abre o formulário para emitir outra nota.</small>
          </span>
          <v-icon class="delivery-choice-arrow" size="20">mdi-chevron-right</v-icon>
        </button>

        <button
          class="delivery-choice-option"
          :disabled="saving"
          @click="printExistingDeliveryNote('reprint')"
        >
          <span class="delivery-choice-icon reprint-note">
            <v-progress-circular
              v-if="saving && selectedExistingNoteAction === 'reprint'"
              indeterminate
              size="22"
              width="2"
              color="primary"
            />
            <v-icon v-else size="23">mdi-printer-outline</v-icon>
          </span>
          <span class="delivery-choice-copy">
            <strong>Reimpressão</strong>
            <small>Imprime novamente a nota atual, mantendo a nota e o agendamento como estão.</small>
          </span>
          <v-icon class="delivery-choice-arrow" size="20">mdi-chevron-right</v-icon>
        </button>
      </v-card-text>
      <v-card-actions class="px-4 pb-4">
        <v-spacer />
        <v-btn text small :disabled="saving" @click="close">Sair</v-btn>
        <v-spacer />
      </v-card-actions>
    </v-card>
  </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import axios from "axios";

export default {
  props: ["printDeliveryNote"],
  data() {
    return {
      opened: false,
      saving: false,
      menu: false,
      dateUnformatted: "",
      visitDate: "",
      visitHour: "",
      vehicle: null,
      responsibleIds: [],
      description: "",
      address: {},
      lastCepSearched: "",
      existingNoteDialog: false,
      selectedExistingNoteAction: "",
      creatingReplacementNote: false
    };
  },
  computed: {
    ...mapGetters(["get_gestordepedido", "get_funcionarios", "get_veiculos"]),
    activeDeliveryNote() {
      const notes = Array.isArray(this.get_gestordepedido.delivery_notes)
        ? this.get_gestordepedido.delivery_notes
        : [];
      for (let index = notes.length - 1; index >= 0; index -= 1) {
        if (notes[index] && notes[index].status !== "canceled") return notes[index];
      }
      return {};
    },
    activeDeliveryNoteNumber() {
      return this.activeDeliveryNote.number || "atual";
    },
    hasExistingDeliveryNote() {
      const sale = this.get_gestordepedido || {};
      return !!(this.activeDeliveryNote && this.activeDeliveryNote._id)
        || !!(sale.external_movimentation && sale.external_movimentation._id);
    },
    vehicleItems() {
      return (this.get_veiculos.docs || [])
        .filter(vehicle => vehicle.ativo !== false)
        .map(vehicle => ({
          ...vehicle,
          label: `${vehicle.placa}${vehicle.modelo ? " - " + vehicle.modelo : ""}`
        }));
    }
  },
  methods: {
    ...mapActions([
      "listar_funcionarios",
      "listar_veiculos",
      "createGlobalMessage"
    ]),
    onlyDigits(value) {
      return String(value || "").replace(/\D/g, "");
    },
    normalizeId(value) {
      if (!value) return null;
      if (typeof value === "string") return value;
      if (value._id) return value._id;
      if (value.id) return value.id;
      return null;
    },
    getDeliveryResponsibleIds(externalMovimentation = {}) {
      return [
        this.normalizeId(externalMovimentation.executor),
        ...(Array.isArray(externalMovimentation.followers)
          ? externalMovimentation.followers.map(follower => this.normalizeId(follower))
          : [])
      ].filter(Boolean);
    },
    deliveryAddressPayload() {
      const address = { ...(this.address || {}) };
      delete address.location;
      delete address.coordinates;
      delete address.geo;
      delete address.position;
      delete address.geometry;
      delete address.formatted_address;
      delete address.geocoded_at;
      delete address.geocoded_by;
      delete address.geocoded_query;
      return address;
    },
    async searchCep() {
      const cep = this.onlyDigits(this.address.zip_code);
      if (cep.length !== 8 || cep === this.lastCepSearched) return;

      this.lastCepSearched = cep;
      try {
        const res = await axios.get(`https://viacep.com.br/ws/${cep}/json/`);
        if (res.status !== 200 || res.data.erro) {
          this.createGlobalMessage({
            type: "warning",
            message: "CEP não encontrado.",
            timeout: 4000
          });
          return;
        }
        this.address.zip_code = cep;
        this.address.street = res.data.logradouro;
        this.address.district = res.data.bairro;
        this.address.city = res.data.localidade;
        this.address.state = res.data.uf;
        this.address.bairro = res.data.bairro;
        this.address.range_numbers = res.data.complemento;
        this.address.number = "";
        this.$nextTick(() => this.$refs.addressNumber && this.$refs.addressNumber.focus());
        this.$forceUpdate();
      } catch (error) {
        this.lastCepSearched = "";
        this.createGlobalMessage({
          type: "warning",
          message: "Não foi possível buscar o CEP.",
          timeout: 4000
        });
      }
    },
    formatDate(date) {
      if (!date) return;
      const [year, month, day] = date.split("-");
      this.visitDate = `${day}/${month}/${year}`;
      this.menu = false;
    },
    async saveAddress() {
      const sale = this.get_gestordepedido;
      const addressDestiny = this.deliveryAddressPayload();
      sale.compra.address = addressDestiny;

      if (!this.$refs.payer_form.validate()) return;

      if (sale.external_movimentation && sale.external_movimentation._id && this.creatingReplacementNote) {
        await this.printExistingDeliveryNote("new");
        return;
      }

      this.saving = true;
      try {

        const response = await this.$run("external_movimentations/create-from-transaction", {
          transaction: sale._id,
          client: sale.compra.cliente,
          address_destiny: addressDestiny,
          type: "delivery",
          name: this.description || `Entrega da venda #${sale.id_transacao || ""}`,
          visit_date: this.visitDate,
          visit_hour: this.visitHour,
          executors: this.responsibleIds,
          vehicle: this.vehicle,
          unity: sale.unity || {}
        });

        if (response.data && response.data.sale) {
          this.$store.commit("set_gestordepedido", response.data.sale);
        }

        this.createGlobalMessage({
          type: "success",
          message: "Entrega criada e nota pronta para impressão.",
          timeout: 4000
        });
        await this.$nextTick();
        this.printDeliveryNote(this.get_gestordepedido);
        this.close();
      } catch (error) {
        this.createGlobalMessage({
          type: "warning",
          message: error.response?.data?.message || "Não foi possível criar a entrega.",
          timeout: 5000
        });
      } finally {
        this.saving = false;
      }
    },
    async printExistingDeliveryNote(mode) {
      this.saving = true;
      this.selectedExistingNoteAction = mode;
      try {
        const sale = this.get_gestordepedido;
        const addressDestiny = this.deliveryAddressPayload();
        const response = await this.$run("transacoes/register-delivery-note-print", {
          _id: sale._id,
          mode,
          address_destiny: addressDestiny,
          visit_date: this.visitDate,
          visit_hour: this.visitHour,
          executors: this.responsibleIds,
          vehicle: this.vehicle,
          cancel_reason: "Nova nota de entrega emitida pelo fluxo de impressão."
        });

        if (response.data && response.data.sale) {
          this.$store.commit("set_gestordepedido", response.data.sale);
        }

        await this.$nextTick();
        this.printDeliveryNote(response.data && response.data.sale ? response.data.sale : this.get_gestordepedido);
        this.createGlobalMessage({
          type: "success",
          message: mode === "new" ? "Nota anterior cancelada e nova nota emitida." : "Nota pronta para reimpressão.",
          timeout: 4000
        });
        this.existingNoteDialog = false;
        this.close();
      } catch (error) {
        this.createGlobalMessage({
          type: "warning",
          message: error.response?.data?.message || "Não foi possível registrar a impressão da nota.",
          timeout: 5000
        });
      } finally {
        this.saving = false;
        this.selectedExistingNoteAction = "";
      }
    },
    startNewDeliveryNote() {
      this.existingNoteDialog = false;
      this.creatingReplacementNote = true;
      this.opened = true;
      this.$nextTick(() => {
        if (this.$refs.cepInput) this.$refs.cepInput.focus();
      });
    },
    open() {
      const sale = this.get_gestordepedido || {};
      const compra = sale.compra || {};
      const externalMovimentation = sale.external_movimentation || {};
      const today = this.$moment ? this.$moment() : null;
      this.address = { ...(externalMovimentation.address_destiny || compra.address || {}) };
      this.responsibleIds = this.getDeliveryResponsibleIds(externalMovimentation);
      this.vehicle = this.normalizeId(externalMovimentation.vehicle);
      this.lastCepSearched = "";
      this.selectedExistingNoteAction = "";
      this.creatingReplacementNote = false;
      this.description = externalMovimentation.name || `Entrega da venda #${sale.id_transacao || ""}`;
      this.dateUnformatted = today ? today.format("YYYY-MM-DD") : "";
      this.visitDate = externalMovimentation.visit_date || (today ? today.format("DD/MM/YYYY") : "");
      this.visitHour = externalMovimentation.visit_hour || (today ? today.format("HH:mm") : "");
      this.listar_funcionarios();
      this.listar_veiculos();
      if (this.hasExistingDeliveryNote) {
        this.existingNoteDialog = true;
        this.opened = false;
        return;
      }
      this.opened = true;
      this.$nextTick(() => {
        if (this.$refs.cepInput) this.$refs.cepInput.focus();
      });
    },
    close() {
      this.opened = false;
      this.existingNoteDialog = false;
      this.creatingReplacementNote = false;
    }
  }
};
</script>

<style scoped>
.delivery-confirmation-modal {
  border-radius: 8px;
}

.delivery-choice-modal {
  border: 1px solid #e3e8ee;
  overflow: hidden;
}

.delivery-confirmation-head {
  align-items: center;
  display: flex;
  gap: 12px;
  padding: 16px 18px 8px;
}

.delivery-confirmation-head strong,
.delivery-confirmation-head small {
  display: block;
}

.delivery-confirmation-head small {
  color: #777;
  font-size: 12px;
}

.delivery-primary-button {
  color: #ffffff !important;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.delivery-choice-head-icon {
  background: #f0f7f5 !important;
  color: #128c7e !important;
}

.delivery-choice-content {
  display: grid;
  gap: 10px;
  padding: 14px 18px 8px !important;
}

.delivery-choice-option {
  align-items: center;
  background: #ffffff;
  border: 1px solid #e3e8ee;
  border-radius: 8px;
  color: #1f2933;
  cursor: pointer;
  display: grid;
  gap: 12px;
  grid-template-columns: 44px minmax(0, 1fr) 20px;
  min-height: 78px;
  padding: 12px;
  text-align: left;
  transition: 0.16s ease;
  width: 100%;
}

.delivery-choice-option:hover {
  border-color: #128c7e;
  box-shadow: 0 10px 26px rgba(18, 140, 126, 0.12);
  transform: translateY(-1px);
}

.delivery-choice-option:disabled {
  cursor: wait;
  opacity: 0.72;
}

.delivery-choice-icon {
  align-items: center;
  border-radius: 8px;
  display: flex;
  height: 44px;
  justify-content: center;
  width: 44px;
}

.delivery-choice-icon.new-note {
  background: #e9f7ef;
  color: #177245;
}

.delivery-choice-icon.reprint-note {
  background: #eef3ff;
  color: #315aa8;
}

.delivery-choice-copy {
  min-width: 0;
}

.delivery-choice-copy strong,
.delivery-choice-copy small {
  display: block;
}

.delivery-choice-copy strong {
  color: #1f2933;
  font-size: 15px;
  line-height: 1.25;
}

.delivery-choice-copy small {
  color: #66717d;
  font-size: 12px;
  line-height: 1.35;
  margin-top: 3px;
}

.delivery-choice-arrow {
  color: #9aa6b2 !important;
}

.delivery-choice-footer {
  display: flex;
  justify-content: center;
  padding: 2px 18px 16px;
}

@media(max-width: 560px) {
  .delivery-choice-option {
    grid-template-columns: 40px minmax(0, 1fr);
  }

  .delivery-choice-arrow {
    display: none;
  }
}
</style>
